import { isDemoCampaignCharacterId } from './demoCampaign';
import {
  buildCampaignSandboxAppKey,
  buildCampaignSandboxInitialLaunch,
  isCampaignSandboxLaunchTarget,
} from './campaignSandboxContract';

export const isLaunchTargetReady = (target, serverViewerContext) => {
  if (!target?.waitForContext) return true;
  if (target.type === 'syntheticDemoCampaign') return true;
  if (isCampaignSandboxLaunchTarget(target)) return true;
  if (serverViewerContext === undefined) return false;
  if (target.type === 'campaignDm') {
    return serverViewerContext?.viewerKind === 'dm'
      && serverViewerContext?.activeCampaignId === target.campaignId;
  }
  if (target.type === 'campaignChar') {
    if (serverViewerContext?.activeCampaignId !== target.campaignId) return false;
    if (serverViewerContext?.viewerKind === 'player') {
      return serverViewerContext?.viewerCharId === target.charId;
    }
    // Real campaign character launches only open when the server viewer
    // context has switched to that player character. Legacy saved-practice
    // rows stay out of live launches; use Campaign Sandbox for local testing.
    return false;
  }
  if (target.type === 'standaloneChar') {
    return serverViewerContext?.viewerKind === 'player'
      && !serverViewerContext?.activeCampaignId
      && serverViewerContext?.viewerCharId === target.charId;
  }
  if (target.type === 'standaloneCreate') {
    return serverViewerContext !== undefined && !serverViewerContext?.activeCampaignId;
  }
  return true;
};

export const isSyntheticDemoCampaignTarget = (target) => (
  target?.type === 'syntheticDemoCampaign'
);

export const isValidSyntheticDemoCampaignTarget = (target) => {
  if (!isSyntheticDemoCampaignTarget(target)) return false;
  if (target.role !== 'player') return true;
  return isDemoCampaignCharacterId(target.charId);
};

export const buildSyntheticDemoInitialLaunch = (target) => {
  if (!isSyntheticDemoCampaignTarget(target)) return null;
  const initialView = target.initialView || null;
  if (target.role === 'player' && !isDemoCampaignCharacterId(target.charId)) {
    return null;
  }
  const base = target.role === 'player' && target.charId
    ? { role: 'player', charId: target.charId }
    : { role: 'dm' };
  const withFeatures = target.enabledFeatures !== undefined
    ? { ...base, enabledFeatures: target.enabledFeatures || null }
    : base;
  return initialView ? { ...withFeatures, initialView } : withFeatures;
};

const buildCampaignLaunchPatch = (target) => {
  const patch = {};
  if (target.campaignId !== undefined) {
    patch.campaignId = target.campaignId || null;
  }
  if (target.enabledFeatures !== undefined) {
    patch.enabledFeatures = target.enabledFeatures || null;
  }
  if (target.homebrew !== undefined) {
    patch.homebrew = target.homebrew || null;
  }
  return patch;
};

// Build the `initialLaunch` payload for a non-demo target. Surfaces the
// optional `initialView` so App.js can route the user directly to a
// specific view (e.g. 'journal', 'dev-dashboard') after mount.
export const buildAuthedInitialLaunch = (target) => {
  if (!target || isSyntheticDemoCampaignTarget(target) || isCampaignSandboxLaunchTarget(target)) return null;
  const initialView = target.initialView || null;
  const campaignPatch = buildCampaignLaunchPatch(target);
  if (target.type === 'campaignDm') {
    const base = { role: 'dm', ...campaignPatch };
    return initialView ? { ...base, initialView } : base;
  }
  if (target.type === 'campaignChar') {
    const base = { role: 'player', charId: target.charId, ...campaignPatch };
    return initialView
      ? { ...base, initialView }
      : base;
  }
  if (target.type === 'standaloneChar') {
    return initialView
      ? { role: 'player', charId: target.charId, initialView }
      : { role: 'player', charId: target.charId };
  }
  if (target.type === 'standaloneCreate') {
    return initialView ? { initialView } : null;
  }
  if (!initialView) return null;
  return { initialView };
};

export const buildAuthedAppKey = (serverViewerContext, viewerContext, launchTarget = null) => {
  if (isSyntheticDemoCampaignTarget(launchTarget)) {
    return `demo:${launchTarget.role || 'dm'}:${launchTarget.charId || 'dm'}`;
  }
  const sandboxKey = buildCampaignSandboxAppKey(launchTarget);
  if (sandboxKey) return sandboxKey;
  return `${serverViewerContext?.activeCampaignId || 'standalone'}:${viewerContext?.viewerKind || 'none'}:${viewerContext?.viewerCharId || 'dm'}`;
};

export {
  buildCampaignSandboxInitialLaunch,
  isCampaignSandboxLaunchTarget,
};
